import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { getFavoriteLists } from '@/gen/bff'
import { zFavoriteListsListDto } from '@/gen/bff/zod.gen'
import { getUserId } from '@/helpers/getUserId'
import { getMultipleFavoriteListKeys } from '@/hooks/bff/favorite/getMultipleFavoriteList/queryKeys'
import { useAsmSession } from '@/hooks/useAsmSession'
import { useSelectedOrgUnitId } from '@/hooks/useSelectedOrgUnitId'
import { useTranslations } from '@/hooks/useTranslations'
import { defaultDataConverter } from '@/lib/converters/defaultDataConverter'
import { defaultZodCatch } from '@/lib/converters/defaultZodCatch'
import { getMultipleFavoriteListConverter } from '@/lib/converters/favorite/getMultipleFavoriteList/getMultipleFavoriteListConverter'

import type { getFavoriteListFull } from '@/gen/bff'
import type { SortByEnumSchema } from '@/schemas/sortDirectionEnumSchema'

export const useGetMultipleFavoriteList = (
  currentPage: number,
  itemsPerPage: number,
  sortBy: SortByEnumSchema,
  searchText: string,
  sortCreatedBy: string,
  options?: { enabled?: boolean }
) => {
  const { translate } = useTranslations()
  const { session, isAuthorized } = useAsmSession()
  const customerId = getUserId(session)
  const orgUnitId = useSelectedOrgUnitId()

  const selectFn = useCallback((res: Awaited<ReturnType<typeof getFavoriteListFull>>) => {
    const responseDto = zFavoriteListsListDto.catch(defaultZodCatch).parse(defaultDataConverter(res))

    return getMultipleFavoriteListConverter(responseDto)
  }, [])

  return useQuery({
    queryKey: getMultipleFavoriteListKeys.list(
      customerId,
      orgUnitId,
      currentPage,
      itemsPerPage,
      sortBy,
      searchText,
      sortCreatedBy
    ),
    queryFn: () =>
      getFavoriteLists({
        body: {
          pageable: {
            page: currentPage,
            pageSize: itemsPerPage,
            sortBy: sortBy,
          },
          searchQuery: searchText,
          searchCreatedBy: sortCreatedBy,
        },
      }),
    enabled: (options?.enabled ?? true) && isAuthorized,
    select: selectFn,
    meta: {
      errorMessage: translate('common.api_error'),
    },
  })
}
